import { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo } from '@/mocks/siteInfo';
import { usePageSEO } from '@/hooks/usePageSEO';
import { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema, siteDomain } from '@/utils/schema';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  usePageSEO({
    title: '关于我们 - kaiyuntiyu | 专业体育平台',
    description: `了解kaiyuntiyu的品牌故事与发展历程。${siteInfo.description.slice(0, 80)}`,
    keywords: 'kaiyuntiyu,kaiyuntiyu官网,kaiyuntiyu平台,kaiyuntiyu品牌,kaiyuntiyu联系方式',
    canonicalUrl: `${siteDomain}/about`,
    schema: [
      getOrganizationSchema(),
      getWebPageSchema('关于我们 - kaiyuntiyu', `了解kaiyuntiyu品牌故事`, `${siteDomain}/about`),
      getBreadcrumbSchema([
        { name: '首页', url: siteDomain },
        { name: '关于我们', url: `${siteDomain}/about` },
      ]),
      getFAQSchema(),
    ],
  });

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = formData.get('phone_alt') as string;
    if (hpValue && hpValue.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(form.getAttribute('action') || '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { name: '首页', path: '/' },
          { name: '关于我们' },
        ]} />
      </div>

      <main>
        {/* Brand Intro */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
                关于 {siteInfo.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                {siteInfo.name}创立于{siteInfo.established}年，{siteInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {siteInfo.values.map((value) => (
                <div key={value.title} className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100/70 rounded-xl">
                    <i className={`${value.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-12">
              发展历程
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {siteInfo.milestones.map((m, index) => (
                  <div key={m.year} className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                      <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 ml-12 md:ml-0">
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-2 whitespace-nowrap">
                          {m.year}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">{m.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-accent-500 border-4 border-background-100 flex items-center justify-center md:-translate-x-1/2 z-10">
                      <div className="w-2.5 h-2.5 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如果您有任何问题或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将在24小时内回复您的咨询。
                </p>
                <div className="space-y-5">
                  {[
                    { icon: 'ri-phone-line', label: '客服热线', value: siteInfo.contact.phone },
                    { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.contact.email },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.contact.address },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: siteInfo.contact.wechat },
                    { icon: 'ri-time-line', label: '工作时间', value: siteInfo.contact.workHours },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-secondary-100/70 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-secondary-600 text-base`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                        <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-5">
                  发送消息
                </h3>
                <form
                  id="about-contact-form"
                  data-readdy-form="true"
                  action="https://readdy.ai/api/form/d9rfv36e3oq4jqumu0vg"
                  onSubmit={handleContactSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-all duration-200 text-sm cursor-pointer border-0 whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-check-line"></i> 消息已成功发送，我们会尽快与您联系！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-error-warning-line"></i> {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {siteInfo.faqs.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                    <span className="font-heading">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}